import { useEffect, useRef, useState } from "react";
import { Activity, Crown, Eye, Play, RefreshCcw, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import PinConfirm from "../components/PinConfirm";
import { FibiLoading } from "../components/FibiLoading";
import { TrCodesDialog } from "../components/TrCodesDialog";

const STATUS_STYLE = {
  ok: { label: "Sudah Dibuat", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  error: { label: "Gagal", cls: "bg-rose-500/10 text-rose-400 border-rose-500/20" },
  belum: { label: "Belum Dibuat", cls: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
};

export default function VoucherTuanRumah() {
  const [data, setData] = useState(null);
  const [run, setRun] = useState(null);
  const [count, setCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const [codesItem, setCodesItem] = useState(null);
  const [selected, setSelected] = useState([]);
  const [genBusy, setGenBusy] = useState({});
  const [delTargets, setDelTargets] = useState(null); // array of {router_id, router_name}
  const pollRef = useRef(null);
  const usagePollRef = useRef(null);
  const [usage, setUsage] = useState(null);
  const [usageBusy, setUsageBusy] = useState(false);
  const [agentData, setAgentData] = useState(null);
  const { user: me } = useAuth();
  const isAdmin = !!me && ["admin", "admin_fasnet"].includes(me.role);

  const load = () => {
    if (!isAdmin) {
      api.get("/tuan-rumah/agent-overview").then((r) => setAgentData(r.data)).catch((e) => toast.error(errMsg(e)));
      return;
    }
    api.get("/tuan-rumah/overview").then((r) => { setData(r.data); setCount(r.data.count_setting); setSelected([]); }).catch((e) => toast.error(errMsg(e)));
    api.get("/tuan-rumah/runs/latest").then((r) => setRun(r.data)).catch(() => {});
    api.get("/tuan-rumah/usage").then((r) => setUsage(r.data)).catch(() => {});
  };
  useEffect(() => { if (me) load(); return () => { clearInterval(pollRef.current); clearInterval(usagePollRef.current); }; }, [me?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  if (me && !isAdmin) {
    const agentItems = agentData?.items || [];
    return (
      <div className="space-y-6" data-testid="voucher-tuan-rumah-agent-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Crown className="w-7 h-7 text-[#F04E23]" /> Voucher Tuan Rumah</h1>
            <p className="text-sm text-muted-foreground mt-1">Kode voucher TUAN-RUMAH bulan {agentData?.month || ""} untuk cabang yang tertaut ke akun Anda. Bersifat hanya-lihat.</p>
          </div>
          <Button variant="outline" onClick={load} data-testid="btn-tr-agent-refresh"><RefreshCcw className="w-4 h-4 mr-1.5" /> Muat Ulang</Button>
        </div>

        <Card className="bg-card border-border shadow-sm overflow-x-auto">
          <CardHeader><CardTitle className="text-base font-heading">Daftar Voucher TUAN-RUMAH Cabang Saya — {agentData?.month || ""}</CardTitle></CardHeader>
          <Table className="min-w-[640px]" data-testid="tr-agent-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead><TableHead>Router</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Jumlah</TableHead><TableHead>Pemakaian</TableHead><TableHead>Kode Voucher</TableHead><TableHead>Waktu</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!agentData && <TableRow><TableCell colSpan={7} className="p-0"><FibiLoading text="FIBI sedang memuat data..." size={20} /></TableCell></TableRow>}
              {agentData && !agentItems.length && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="tr-agent-empty">Tidak ada router yang tertaut ke akun Anda. Hubungi admin untuk menautkan cabang.</TableCell></TableRow>}
              {agentItems.map((it, idx) => {
                const st = STATUS_STYLE[it.status] || STATUS_STYLE.belum;
                return (
                  <TableRow key={it.router_id} data-testid={`tr-agent-row-${it.router_name}`}>
                    <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                    <TableCell className="font-medium text-sm">{it.router_name}</TableCell>
                    <TableCell><span className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono border ${st.cls}`}>{st.label}</span></TableCell>
                    <TableCell className="text-right font-mono">{it.count}</TableCell>
                    <TableCell>
                      {it.usage ? (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono border whitespace-nowrap ${it.usage.dipakai > 0 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-slate-500/10 text-slate-400 border-slate-500/20"}`}>
                          {it.usage.dipakai}/{it.usage.total} dipakai
                        </span>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      {it.codes.length ? (
                        <button type="button" onClick={() => setCodesItem(it)} className="inline-flex items-center gap-1 text-xs text-sky-400 hover:underline font-mono" data-testid={`tr-agent-codes-${it.router_name}`}>
                          <Eye className="w-3.5 h-3.5" /> {it.codes[0]}{it.codes.length > 1 ? ` +${it.codes.length - 1}` : ""}
                        </button>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">{it.created_at ? fmtDateTime(it.created_at) : "-"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        <TrCodesDialog item={codesItem} onClose={() => setCodesItem(null)} onCopy={copyAllCodes} onShare={shareCodesWA} />
      </div>
    );
  }

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const { data: d } = await api.put("/tuan-rumah/settings", { count: Number(count) });
      toast.success(`Jumlah voucher per router diset ${d.count}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data: r } = await api.get("/tuan-rumah/runs/latest");
        setRun(r);
        if (r.status === "done") {
          clearInterval(pollRef.current);
          setBusy(false);
          toast.success(`Selesai: ${r.created} cabang dibuat, ${r.skipped} sudah ada, ${r.failed} gagal`);
          load();
        }
      } catch { /* abaikan */ }
    }, 5000);
  };

  const startUsagePolling = () => {
    clearInterval(usagePollRef.current);
    usagePollRef.current = setInterval(async () => {
      try {
        const { data: u } = await api.get("/tuan-rumah/usage");
        setUsage(u);
        if (u.scan?.status === "done") {
          clearInterval(usagePollRef.current);
          setUsageBusy(false);
          toast.success(`Pemindaian selesai: ${u.scan.dipakai ?? 0} voucher terpakai dari ${u.scan.scanned ?? 0} cabang`);
        }
      } catch { /* abaikan */ }
    }, 5000);
  };

  const scanUsage = async () => {
    setUsageBusy(true);
    try {
      await api.post("/tuan-rumah/usage-scan");
      toast.info("Pemindaian pemakaian dimulai — mengecek kode live di setiap router");
      startUsagePolling();
    } catch (err) { toast.error(errMsg(err)); setUsageBusy(false); }
  };

  const generateMassal = async () => {
    setBusy(true);
    try {
      await api.post("/tuan-rumah/generate-massal");
      toast.info("Pembuatan masal dimulai — hanya router yang belum punya voucher bulan ini yang diproses");
      startPolling();
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };

  const genOne = async (it) => {
    setGenBusy((p) => ({ ...p, [it.router_id]: true }));
    try {
      const { data: d } = await api.post(`/tuan-rumah/generate/${it.router_id}`);
      toast.success(d.added !== undefined ? `+${d.added} voucher ditambahkan untuk ${it.router_name} (total ${d.count})` : `${d.count} voucher TUAN-RUMAH dibuat untuk ${it.router_name}`);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally {
      setGenBusy((p) => ({ ...p, [it.router_id]: false }));
    }
  };

  const doExclude = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/tuan-rumah/exclude", { router_ids: delTargets.map((t) => t.router_id) });
      toast.success(`${d.excluded} router dihapus dari daftar Voucher Tuan Rumah`);
      setDelTargets(null);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const doInclude = async (item) => {
    try {
      await api.post("/tuan-rumah/include", { router_ids: [item.router_id] });
      toast.success(`${item.router_name} dikembalikan ke daftar`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const copyAllCodes = () => {
    const codes = codesItem?.codes || [];
    const text = `Voucher Tuan Rumah - ${codesItem?.router_name || ""}\n${codes.map((c, i) => `${i + 1}. ${c}`).join("\n")}`;
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${codes.length} kode voucher disalin ke clipboard`))
      .catch(() => toast.error("Gagal menyalin — salin manual dari daftar"));
  };

  const saveRouterCount = async (it, raw) => {
    const v = raw === "" ? null : Math.max(1, Math.min(20, Number(raw) || 0));
    if (v === it.target_count || (v === null && !it.custom_count)) return;
    try {
      const { data: r } = await api.put(`/tuan-rumah/router-count/${it.router_id}`, { count: v });
      toast.success(`${it.router_name}: jumlah voucher ${r.custom ? `khusus ${r.count}` : `kembali ke default ${r.count}`}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const shareCodesWA = () => {
    if (!codesItem) return;
    const text = `Voucher Tuan Rumah - ${codesItem.router_name}\n${(codesItem.codes || []).map((c, i) => `${i + 1}. ${c}`).join("\n")}\n\nUsername = password • Masa aktif 30 hari sejak login pertama`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const items = data?.items || [];
  const usageItems = usage?.items || [];
  const usageMap = Object.fromEntries(usageItems.map((u) => [u.router_id, u]));
  const totalKode = usageItems.reduce((s, u) => s + (u.total || 0), 0);
  const totalDipakai = usageItems.reduce((s, u) => s + (u.dipakai || 0), 0);
  const totalBelum = usageItems.reduce((s, u) => s + (u.belum || 0), 0);
  const totalHilang = usageItems.reduce((s, u) => s + (u.hilang || 0), 0);
  const excluded = data?.excluded || [];
  const done = items.filter((i) => i.status === "ok").length;
  const failed = items.filter((i) => i.status === "error").length;
  const pending = items.length - done - failed;
  const allChecked = items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allChecked ? [] : items.map((i) => i.router_id));
  const toggleOne = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="space-y-6" data-testid="voucher-tuan-rumah-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Crown className="w-7 h-7 text-[#F04E23]" /> Voucher Tuan Rumah</h1>
          <p className="text-sm text-muted-foreground mt-1">Voucher profile TUAN-RUMAH dibuat otomatis setiap tanggal 1 di semua router. Halaman & profile ini khusus admin — tidak terlihat oleh agen.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.length > 0 && (
            <Button variant="destructive" onClick={() => setDelTargets(items.filter((i) => selected.includes(i.router_id)))} data-testid="btn-tr-delete-selected">
              <Trash2 className="w-4 h-4 mr-1.5" /> Hapus Terpilih ({selected.length})
            </Button>
          )}
          <Button variant="outline" onClick={scanUsage} disabled={usageBusy} title="Cek live pemakaian kode TUAN-RUMAH di semua router" data-testid="btn-tr-usage-scan">
            <Activity className={`w-4 h-4 mr-1.5 ${usageBusy ? "animate-pulse text-emerald-400" : ""}`} /> {usageBusy ? "Memindai..." : "Cek Pemakaian"}
          </Button>
          <Button variant="outline" onClick={load} data-testid="btn-tr-refresh"><RefreshCcw className="w-4 h-4 mr-1.5" /> Muat Ulang</Button>
          <Button onClick={generateMassal} disabled={busy} data-testid="btn-tr-generate-massal">
            <Play className="w-4 h-4 mr-1.5" /> {busy ? "Sedang Berjalan..." : "Buat Masal Voucher TUAN-RUMAH"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="text-base font-heading">Pengaturan Jumlah</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={saveSettings} className="flex items-end gap-3">
              <div className="space-y-1.5 flex-1">
                <Label className="text-xs">Jumlah voucher per router per bulan (1-20)</Label>
                <Input type="number" min="1" max="20" value={count} onChange={(e) => setCount(e.target.value)} data-testid="input-tr-count" />
              </div>
              <Button type="submit" variant="secondary" data-testid="btn-tr-save-settings"><Save className="w-4 h-4 mr-1.5" /> Simpan</Button>
            </form>
            <p className="text-[11px] text-muted-foreground mt-2">Berlaku untuk pembuatan otomatis tanggal 1 dan tombol Buat Masal. Username = password acak, masa aktif 30 hari.</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border" data-testid="tr-run-status">
          <CardHeader><CardTitle className="text-base font-heading">Status Pembuatan {data?.month || ""}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex gap-4 text-sm font-mono">
              <span className="text-emerald-400" data-testid="tr-done-count">{done} dibuat</span>
              <span className="text-slate-400" data-testid="tr-pending-count">{pending} belum</span>
              <span className="text-rose-400" data-testid="tr-failed-count">{failed} gagal</span>
            </div>
            {run && run.status !== "never" && (
              <p className="text-[11px] text-muted-foreground" data-testid="tr-last-run">
                Terakhir: {run.status === "running" ? "sedang berjalan..." : `selesai ${fmtDateTime(run.finished_at)}`} — {run.triggered_by} ({run.created ?? 0} dibuat, {run.skipped ?? 0} dilewati, {run.failed ?? 0} gagal)
              </p>
            )}
            {usage?.scan?.status === "running" && <p className="text-[11px] text-emerald-400 animate-pulse" data-testid="tr-usage-scanning">Pemindaian pemakaian sedang berjalan...</p>}
            {usageItems.length > 0 && (
              <div className="flex gap-3 text-[11px] font-mono flex-wrap pt-2 border-t border-border" data-testid="tr-usage-summary">
                <span className="text-muted-foreground">Pemakaian: <b className="text-foreground">{totalKode} kode</b></span>
                <span className="text-emerald-400">{totalDipakai} dipakai</span>
                <span className="text-amber-400">{totalBelum} belum</span>
                <span className="text-rose-400">{totalHilang} hilang</span>
                {usage?.scan?.finished_at && <span className="text-muted-foreground ml-auto">scan {fmtDateTime(usage.scan.finished_at)}</span>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="tr-table-card">
        <CardHeader><CardTitle className="text-base font-heading">Daftar Voucher TUAN-RUMAH per Router — {data?.month || ""}</CardTitle></CardHeader>
        <Table className="min-w-[920px]" data-testid="tr-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input type="checkbox" className="accent-[#F04E23] w-4 h-4 cursor-pointer" checked={allChecked} onChange={toggleAll} title="Pilih semua" data-testid="chk-tr-all" />
              </TableHead>
              <TableHead className="w-10">#</TableHead>
              <TableHead className="w-36">Buat Voucher</TableHead><TableHead>Router</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Jumlah</TableHead><TableHead>Pemakaian</TableHead><TableHead>Kode Voucher</TableHead><TableHead>Sumber</TableHead><TableHead>Waktu</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!items.length && <TableRow><TableCell colSpan={11} className="p-0" data-testid="tr-empty"><FibiLoading text="FIBI sedang memuat data..." size={20} /></TableCell></TableRow>}
            {items.map((it, idx) => {
              const st = STATUS_STYLE[it.status] || STATUS_STYLE.belum;
              return (
                <TableRow key={it.router_id} className={`${it.status === "error" ? "bg-rose-500/5" : ""} ${selected.includes(it.router_id) ? "bg-sky-500/5" : ""}`} data-testid={`tr-row-${it.router_name}`}>
                  <TableCell>
                    <input type="checkbox" className="accent-[#F04E23] w-4 h-4 cursor-pointer" checked={selected.includes(it.router_id)} onChange={() => toggleOne(it.router_id)} data-testid={`chk-tr-${it.router_name}`} />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                  <TableCell className="pr-10">
                    {(() => { const need = it.status === "ok" ? it.target_count - it.count : 0; const can = it.status !== "ok" || need > 0; return (
                    <Button size="sm" variant="outline" className="h-7 text-[11px] text-[#F04E23] border-[#F04E23]/40 hover:bg-[#F04E23]/10"
                      disabled={!can || genBusy[it.router_id]}
                      title={it.status === "ok" && need <= 0 ? "Naikkan angka Jumlah untuk menambah voucher" : undefined}
                      onClick={() => genOne(it)} data-testid={`btn-tr-gen-${it.router_name}`}>
                      <Play className="w-3 h-3 mr-1" /> {genBusy[it.router_id] ? "Membuat..." : it.status === "ok" ? (need > 0 ? `Buat Lagi (+${need})` : "Sudah Ada") : "Buat Voucher"}
                    </Button>); })()}
                  </TableCell>
                  <TableCell className="font-medium text-sm">{it.router_name}</TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono border ${st.cls}`} data-testid={`tr-status-${it.router_name}`}>{st.label}</span>
                    {it.error && <div className="text-[10px] text-rose-400 mt-0.5 max-w-48 truncate" title={it.error}>{it.error}</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono" data-testid={`tr-count-${it.router_name}`}>
                    <div className="inline-flex items-center gap-1">
                      <button type="button" className="w-6 h-7 rounded border border-border hover:bg-accent text-sm" onClick={() => saveRouterCount(it, String(Math.max(1, it.target_count - 1)))} data-testid={`btn-tr-count-dec-${it.router_name}`}>−</button>
                      <input type="number" min={1} max={20} defaultValue={it.target_count} key={`${it.router_id}-${it.target_count}`}
                        onBlur={(e) => saveRouterCount(it, e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        title={`${it.status === "ok" ? `Sudah dibuat ${it.count}. ` : ""}${it.custom_count ? "Jumlah khusus router ini (dipakai juga bulan depan)" : `Default ${data?.count_setting} — ketik untuk ubah khusus router ini`}`}
                        className={`w-12 h-7 rounded border bg-background px-1 text-center font-mono text-sm ${it.custom_count ? "border-[#F04E23]/60 text-[#F04E23]" : "border-border"}`}
                        data-testid={`inp-tr-count-${it.router_name}`} />
                      <button type="button" className="w-6 h-7 rounded border border-border hover:bg-accent text-sm" onClick={() => saveRouterCount(it, String(Math.min(20, it.target_count + 1)))} data-testid={`btn-tr-count-inc-${it.router_name}`}>+</button>
                    </div>
                    {it.status === "ok" && it.count !== it.target_count && <div className="text-[10px] text-muted-foreground">dibuat {it.count}</div>}
                  </TableCell>
                  <TableCell data-testid={`tr-usage-${it.router_name}`}>
                    {usageMap[it.router_id] ? (
                      usageMap[it.router_id].status === "ok" ? (
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-mono border whitespace-nowrap ${usageMap[it.router_id].dipakai > 0 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-slate-500/10 text-slate-400 border-slate-500/20"}`}
                          title={`Dipakai ${usageMap[it.router_id].dipakai} • Belum ${usageMap[it.router_id].belum} • Hilang ${usageMap[it.router_id].hilang}`}>
                          {usageMap[it.router_id].dipakai}/{usageMap[it.router_id].total} dipakai
                        </span>
                      ) : <span className="text-[10px] text-muted-foreground" title={usageMap[it.router_id].error}>offline</span>
                    ) : <span className="text-xs text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell>
                    {it.codes.length ? (
                      <button type="button" onClick={() => setCodesItem(it)} className="inline-flex items-center gap-1 text-xs text-sky-400 hover:underline font-mono" data-testid={`tr-codes-${it.router_name}`}>
                        <Eye className="w-3.5 h-3.5" /> {it.codes[0]}{it.codes.length > 1 ? ` +${it.codes.length - 1}` : ""}
                      </button>
                    ) : <span className="text-xs text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">{it.source === "cron" ? "Otomatis" : it.source ? "Manual" : "-"}</TableCell>
                  <TableCell className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">{it.created_at ? fmtDateTime(it.created_at) : "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="text-rose-400 h-8 w-8" title="Hapus dari daftar" onClick={() => setDelTargets([it])} data-testid={`btn-tr-del-${it.router_name}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {excluded.length > 0 && (
        <Card className="bg-card border-border" data-testid="tr-excluded-card">
          <CardHeader><CardTitle className="text-base font-heading text-muted-foreground">Router Dihapus dari Daftar ({excluded.length})</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {excluded.map((x) => (
              <span key={x.router_id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-accent/30 text-xs font-mono" data-testid={`tr-excluded-${x.router_name}`}>
                {x.router_name}
                <button type="button" onClick={() => doInclude(x)} className="text-emerald-400 hover:underline inline-flex items-center gap-0.5" title="Kembalikan ke daftar" data-testid={`btn-tr-restore-${x.router_name}`}>
                  <RotateCcw className="w-3 h-3" /> Pulihkan
                </button>
              </span>
            ))}
          </CardContent>
        </Card>
      )}

      <TrCodesDialog item={codesItem} onClose={() => setCodesItem(null)} onCopy={copyAllCodes} onShare={shareCodesWA} />

      <PinConfirm open={!!delTargets} onOpenChange={() => setDelTargets(null)}
        title={`Hapus ${delTargets?.length > 1 ? `${delTargets.length} Router` : delTargets?.[0]?.router_name} dari Daftar?`}
        description={`Router dikeluarkan dari daftar Voucher Tuan Rumah dan tidak lagi dibuatkan voucher otomatis tiap tanggal 1. Data voucher bulan ini untuk router tersebut ikut dihapus dari daftar. Router tidak terhapus dari sistem dan bisa dipulihkan kapan saja.${delTargets?.length > 1 ? ` Router: ${delTargets.map((t) => t.router_name).join(", ")}` : ""}`}
        confirmLabel={busy ? "Memproses..." : "Ya, Hapus"} destructive onConfirm={doExclude} testid="pin-tr-delete" />
    </div>
  );
}
